import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import EmailEditor from "react-email-editor";
import { ArrowLeft, Save, Send, Loader2 } from "lucide-react";
import { useAppBase } from "@/hooks/useAppBase";

/**
 * Full-page email template editor (Unlayer).
 * Route: /app/email/templates/:id  (id = "new" for creation)
 *
 * Variables ("merge tags") are inserted via Unlayer's built-in `{}` menu inside
 * the text-editing toolbar — we don't duplicate a sidebar.
 */
export default function EmailTemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const base = useAppBase();
  const isNew = id === "new";
  const editorRef = useRef(null);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [initialDesign, setInitialDesign] = useState(null);
  const [ready, setReady] = useState(isNew);
  const [savedId, setSavedId] = useState(isNew ? null : id);
  const [showTest, setShowTest] = useState(false);
  const wrapRef = useRef(null);
  const [editorH, setEditorH] = useState(600);

  useLayoutEffect(() => {
    const compute = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setEditorH(Math.max(400, Math.floor(rect.height)));
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [ready]);

  useEffect(() => {
    if (isNew) return;
    api.get(`/email/templates/${id}`).then((r) => {
      const t = r.data;
      setName(t.name || "");
      setSubject(t.subject || "");
      if (t.design) setInitialDesign(t.design);
      setReady(true);
    }).catch(() => { toast.error("Template non trovato"); nav(`${base}/email`); });
  }, [id, isNew, nav]);

  const onEditorLoad = (unlayer) => {
    editorRef.current = unlayer;
    if (initialDesign && unlayer.loadDesign) unlayer.loadDesign(initialDesign);
    if (unlayer.setMergeTags) unlayer.setMergeTags(MERGE_TAGS);
  };
  useEffect(() => {
    if (initialDesign && editorRef.current?.loadDesign) editorRef.current.loadDesign(initialDesign);
  }, [initialDesign]);

  const exportAndSave = () =>
    new Promise((resolve, reject) => {
      const unlayer = editorRef.current;
      if (!unlayer) return reject(new Error("Editor non pronto"));
      unlayer.exportHtml((data) => resolve({ design: data.design, html: data.html }));
    });

  const save = async () => {
    if (!name.trim() || !subject.trim()) { toast.error("Nome e oggetto obbligatori"); return; }
    setSaving(true);
    try {
      const { design, html } = await exportAndSave();
      const payload = { name: name.trim(), subject: subject.trim(), html, kind: "marketing", design };
      if (isNew || !savedId) {
        const r = await api.post("/email/templates", payload);
        setSavedId(r.data.id);
        window.history.replaceState(null, "", `/app/email/templates/${r.data.id}`);
      } else {
        await api.patch(`/email/templates/${savedId}`, payload);
      }
      toast.success("Template salvato");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Errore"); }
    finally { setSaving(false); }
  };

  const testSend = async () => {
    if (!savedId) { toast.error("Salva prima il template"); return; }
    if (!testTo) { toast.error("Inserisci un'email"); return; }
    setTesting(true);
    try {
      const r = await api.post(`/email/templates/${savedId}/test-send`, { to: testTo, template_id: savedId });
      if (r.data.ok) { toast.success(`Test inviato a ${testTo}`); setShowTest(false); }
      else toast.error(r.data.error || "Errore invio");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setTesting(false); }
  };

  return (
    <div data-testid="tpl-editor-page" className="fixed inset-0 md:left-64 bg-[color:var(--sp-bg)] flex flex-col z-30">
      {/* Compact header — Indietro + nome a sinistra, Invia test + Salva a destra */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--sp-border)] bg-white flex-shrink-0 flex-wrap">
        <button onClick={() => nav(`${base}/email`)} className="sp-btn-ghost inline-flex items-center gap-1 text-sm" data-testid="tpl-back">
          <ArrowLeft className="h-4 w-4" /> Indietro
        </button>
        <div className="h-6 w-px bg-[color:var(--sp-border)]" />
        <input
          data-testid="tpl-name"
          className="text-lg font-display font-black bg-transparent border-0 outline-none focus:ring-0 min-w-0 flex-1 placeholder:text-[color:var(--sp-text-muted)]"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder={isNew ? "Nome template (es. Promo Compleanno)" : ""}
        />
        {savedId && (
          <button
            data-testid="tpl-open-test"
            onClick={() => setShowTest(true)}
            className="sp-btn-ghost inline-flex items-center gap-1 text-sm"
          >
            <Send className="h-4 w-4" /> Invia test
          </button>
        )}
        <button data-testid="tpl-save" onClick={save} disabled={saving} className="sp-btn-primary inline-flex items-center gap-2 text-sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
        </button>
      </div>

      {/* Subject bar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-[color:var(--sp-border)] bg-white flex-shrink-0">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)] flex-shrink-0">Oggetto</label>
        <input
          data-testid="tpl-subject"
          className="flex-1 bg-transparent border-0 outline-none text-sm py-1.5"
          value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Ciao {{ client.name }}, un regalo per te 🎁 — puoi usare {{ salon.name }}"
        />
      </div>

      {/* Full-height Unlayer canvas */}
      <div ref={wrapRef} className="flex-1 min-h-0 bg-white overflow-hidden" data-testid="tpl-unlayer-wrap">
        {ready ? (
          <EmailEditor
            ref={editorRef}
            onReady={onEditorLoad}
            minHeight={`${editorH}px`}
            options={{
              displayMode: "email",
              locale: "it-IT",
              translations: UNLAYER_IT_TRANSLATIONS,
              mergeTags: MERGE_TAGS,
              mergeTagsConfig: { autocompleteTriggerChar: "@" },
              features: {
                textEditor: {
                  emojis: true,
                  cleanPaste: true,
                  tables: true,
                },
                preview: true,
                undoRedo: true,
              },
              appearance: {
                theme: "modern_light",
                panels: {
                  tools: { dock: "left" },
                },
                loader: { html: "<div style='font-family:Manrope,system-ui;padding:24px;color:#64748B'>Caricamento editor…</div>" },
              },
              customCSS: UNLAYER_CUSTOM_CSS,
              tools: {
                image: { properties: { src: { value: { url: "https://placehold.co/560x240?text=Immagine" } } } },
              },
            }}
          />
        ) : (
          <div className="flex items-center justify-center py-24 text-[color:var(--sp-text-muted)]">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento template…
          </div>
        )}
      </div>

      {/* Test send modal */}
      {showTest && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowTest(false)}>
          <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-black mb-4">Invia una copia di prova</h3>
            <input
              data-testid="tpl-test-to"
              type="email"
              className="sp-input"
              placeholder="destinatario@esempio.com"
              value={testTo} onChange={(e) => setTestTo(e.target.value)}
            />
            <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-2">Riceverai una copia con dati di esempio ({"{{ client.name }}"} → Mario Rossi).</div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowTest(false)} className="sp-btn-ghost flex-1">Annulla</button>
              <button data-testid="tpl-test-send" onClick={testSend} disabled={testing} className="sp-btn-primary flex-1 inline-flex items-center justify-center gap-2">
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Campi personalizzati — appaiono nel menu "{}" della toolbar di Unlayer.
   Raggruppati per categoria per rendere l'elenco leggibile. */
const MERGE_TAGS = {
  client: {
    name: "Cliente",
    mergeTags: {
      name:              { name: "Nome completo",       value: "{{ client.name }}",              sample: "Mario Rossi" },
      first_name:        { name: "Solo nome",           value: "{{ client.first_name }}",        sample: "Mario" },
      email:             { name: "Email",               value: "{{ client.email }}",             sample: "mario@example.com" },
      phone:             { name: "Telefono",            value: "{{ client.phone }}",             sample: "+39 333 1234567" },
      birthday:          { name: "Compleanno",          value: "{{ client.birthday }}",          sample: "14 mag" },
      next_appointment:  { name: "Prossimo appuntamento", value: "{{ client.next_appointment }}", sample: "20 mag 2026, 14:30" },
      last_appointment:  { name: "Ultima visita",       value: "{{ client.last_appointment }}",  sample: "10 gen 2026" },
      last_service:      { name: "Ultimo servizio",     value: "{{ client.last_service }}",      sample: "Taglio + Piega" },
    },
  },
  salon: {
    name: "Salone",
    mergeTags: {
      name:    { name: "Nome salone",  value: "{{ salon.name }}",    sample: "Il tuo salone" },
      address: { name: "Indirizzo",    value: "{{ salon.address }}", sample: "Via Roma 12, Milano" },
      phone:   { name: "Telefono",     value: "{{ salon.phone }}",   sample: "+39 02 1234567" },
    },
  },
  system: {
    name: "Sistema",
    mergeTags: {
      unsub: { name: "Link disiscrizione", value: "{{ unsubscribe_url }}", sample: "https://memento-app.com/u/token" },
    },
  },
};

/* Traduzione italiana per Unlayer: formato "flat dotted keys" richiesto dal builder.
 * Il locale it-IT è built-in su Unlayer free ma alcune label restano in EN se
 * mancano nel pacchetto: forziamo qui le più visibili + rebranding
 * "merge tags" → "campi personalizzati". */
const UNLAYER_IT_TRANSLATIONS = {
  "it-IT": {
    // Merge tags menu
    "labels.merge_tags": "Campi personalizzati",
    "labels.merge_tag": "Campo personalizzato",
    "labels.special_links": "Link speciali",
    // Sidebar tools tabs
    "tools.tabs.content": "Contenuto",
    "tools.tabs.body": "Corpo",
    "tools.tabs.blocks": "Blocchi",
    "tools.tabs.row": "Riga",
    // Tool titles (drag & drop palette)
    "tools.text.title": "Testo",
    "tools.heading.title": "Titolo",
    "tools.image.title": "Immagine",
    "tools.button.title": "Bottone",
    "tools.divider.title": "Divisore",
    "tools.html.title": "HTML",
    "tools.menu.title": "Menu",
    "tools.social.title": "Social",
    "tools.video.title": "Video",
    "tools.form.title": "Modulo",
    "tools.columns.title": "Colonne",
    "tools.timer.title": "Timer",
    "tools.icons.title": "Icone",
    "tools.stickers.title": "Sticker",
    "tools.gif.title": "GIF",
    // Content section headings
    "labels.content": "Contenuto",
    "labels.body": "Corpo",
    "labels.rows": "Righe",
    "labels.blocks": "Blocchi",
    "labels.preview": "Anteprima",
    "labels.undo": "Annulla",
    "labels.redo": "Ripristina",
    // Buttons/actions
    "actions.save": "Salva",
    "actions.cancel": "Annulla",
    "actions.delete": "Elimina",
    "actions.duplicate": "Duplica",
  },
};

/* CSS custom iniettato nell'iframe Unlayer per allineare i bottoni/select al tema
 * Memento (angoli arrotondati, spaziature morbide, focus brand-colored). */
const UNLAYER_CUSTOM_CSS = `
  button, .btn, input[type=\"button\"], input[type=\"submit\"] {
    border-radius: 10px !important;
  }
  select, input[type=\"text\"], input[type=\"number\"], input[type=\"email\"], textarea {
    border-radius: 10px !important;
  }
  .blockbuilder-branding, .blockbuilder-preferences-tab { font-family: 'Manrope', system-ui, sans-serif !important; }
  .actions-container button.btn.btn-primary,
  .btn.btn-primary {
    border-radius: 10px !important;
  }
`;

